import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ShieldCheck, Search, AlertTriangle, CheckCircle, Clock, Lock, Shield, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function VulnerabilityScanning() {
  const [scans, setScans] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [domain, setDomain] = useState('');
  const [currentScan, setCurrentScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vulnerability/scans`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (error) {
      toast.error('Failed to load scans');
    }
  };

  const handleScan = async () => {
    if (!domain) {
      toast.error('Please enter a domain to scan');
      return;
    }

    setScanning(true);
    toast.info('Starting vulnerability scan... This may take up to 30 seconds.');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/vulnerability/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ domain })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Scan completed successfully');
        setCurrentScan(data);
        setDomain('');
        fetchScans();
      } else {
        throw new Error('Scan failed');
      }
    } catch (error) {
      toast.error('Failed to complete scan. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-primary';
    if (grade.startsWith('B')) return 'text-accent';
    if (grade.startsWith('C')) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getGradeBg = (grade) => {
    if (!grade) return 'bg-muted/20 border-muted/30';
    if (grade.startsWith('A')) return 'bg-primary/10 border-primary/30';
    if (grade.startsWith('B')) return 'bg-accent/10 border-accent/30';
    if (grade.startsWith('C')) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-destructive/10 border-destructive/30';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-primary';
    if (score >= 75) return 'text-accent';
    if (score >= 60) return 'text-yellow-500';
    return 'text-destructive';
  };

  return (
    <DashboardLayout title="Vulnerability Scanning">
      <div className="space-y-6" data-testid="vulnerability-scanning-page">
        {/* Header with Info */}
        <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-mono font-bold mb-2">Website Vulnerability Scanner</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Scan your domains for security vulnerabilities, SSL/TLS configuration issues, and missing security headers.
                Powered by Mozilla Observatory and SSL Labs.
              </p>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  SSL/TLS Analysis
                </span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  Security Headers
                </span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  HTTPS Configuration
                </span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  Best Practice Audit
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scan Initiation */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Scan Your Website
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Enter your domain to check for security vulnerabilities, SSL configuration, and security headers
          </p>
          
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="domain" className="font-mono mb-2 block">Domain to Scan</Label>
              <Input
                id="domain"
                placeholder="example.com or https://example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="font-mono"
                data-testid="domain-input"
                onKeyDown={(e) => e.key === 'Enter' && !scanning && handleScan()}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleScan}
                disabled={scanning || !domain}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono"
                data-testid="scan-button"
              >
                {scanning ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Scan Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Current Scan Results */}
        {currentScan && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-mono font-bold mb-4">Latest Scan Results</h3>
            <ScanResults scan={currentScan} />
          </div>
        )}

        {/* Scan History */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4">Scan History</h3>
          {scans.length === 0 ? (
            <div className="text-center py-8">
              <ShieldCheck className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No vulnerability scans performed yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => (
                <div
                  key={scan.scan_id}
                  className="p-4 bg-background border border-border rounded-md hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setCurrentScan(scan)}
                  data-testid={`scan-item-${scan.scan_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono font-bold">{scan.domain}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-mono border ${
                          scan.status === 'completed' 
                            ? 'bg-primary/10 text-primary border-primary/20' 
                            : 'bg-accent/10 text-accent border-accent/20'
                        }`}>
                          {scan.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                        {scan.ssl_grade && (
                          <span className="flex items-center gap-1">
                            <Lock className="h-3 w-3" />
                            SSL: <span className={getGradeColor(scan.ssl_grade)}>{scan.ssl_grade}</span>
                          </span>
                        )}
                        {scan.headers_grade && (
                          <span className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            Headers: <span className={getGradeColor(scan.headers_grade)}>{scan.headers_grade}</span>
                          </span>
                        )}
                        {scan.overall_score && (
                          <span className="flex items-center gap-1">
                            Score: <span className={getScoreColor(scan.overall_score)}>{scan.overall_score}/100</span>
                          </span>
                        )}
                        <span>{new Date(scan.started_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function ScanResults({ scan }) {
  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-primary';
    if (grade.startsWith('B')) return 'text-accent';
    if (grade.startsWith('C')) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getGradeBg = (grade) => {
    if (!grade) return 'bg-muted/20 border-muted/30';
    if (grade.startsWith('A')) return 'bg-primary/10 border-primary/30';
    if (grade.startsWith('B')) return 'bg-accent/10 border-accent/30';
    if (grade.startsWith('C')) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-destructive/10 border-destructive/30';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-primary';
    if (score >= 75) return 'text-accent';
    if (score >= 60) return 'text-yellow-500';
    return 'text-destructive';
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`border rounded-md p-4 ${getGradeBg(scan.ssl_grade)}`}>
          <div className="flex items-center gap-2 mb-2">
            <Lock className="h-4 w-4 text-accent" />
            <span className="text-sm font-mono text-muted-foreground">SSL Grade</span>
          </div>
          <p className={`text-4xl font-mono font-bold ${getGradeColor(scan.ssl_grade)}`}>
            {scan.ssl_grade || 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">SSL Labs Rating</p>
        </div>

        <div className={`border rounded-md p-4 ${getGradeBg(scan.headers_grade)}`}>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-sm font-mono text-muted-foreground">Headers Grade</span>
          </div>
          <p className={`text-4xl font-mono font-bold ${getGradeColor(scan.headers_grade)}`}>
            {scan.headers_grade || 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Mozilla Observatory</p>
        </div>

        <div className="bg-background border border-border rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-accent" />
            <span className="text-sm font-mono text-muted-foreground">Overall Score</span>
          </div>
          <p className={`text-4xl font-mono font-bold ${getScoreColor(scan.overall_score || 0)}`}>
            {scan.overall_score || 0}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Out of 100</p>
        </div>

        <div className="bg-background border border-border rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-mono text-muted-foreground">Scan Time</span>
          </div>
          <p className="text-xl font-mono font-bold">
            {scan.completed_at 
              ? `${Math.round((new Date(scan.completed_at) - new Date(scan.started_at)) / 1000)}s`
              : 'In progress'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{new Date(scan.started_at).toLocaleString()}</p>
        </div>
      </div>

      {/* Recommendations */}
      {scan.recommendations && scan.recommendations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-md font-mono font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-accent" />
            Security Recommendations
          </h4>
          <div className="space-y-2">
            {scan.recommendations.map((rec, index) => (
              <div 
                key={index} 
                className={`flex items-start gap-3 p-3 rounded-md border ${
                  rec.includes('no critical') 
                    ? 'bg-primary/5 border-primary/20' 
                    : 'bg-accent/5 border-accent/20'
                }`}
              >
                {rec.includes('no critical') ? (
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                )}
                <span className="text-sm font-mono">{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SSL Details */}
      {scan.ssl_results && !scan.ssl_results.error && scan.ssl_results.endpoints && (
        <div className="space-y-3">
          <h4 className="text-md font-mono font-bold flex items-center gap-2">
            <Lock className="h-4 w-4 text-accent" />
            SSL/TLS Details
          </h4>
          <div className="bg-background border border-border rounded-md p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-mono">
              <div>
                <p className="text-muted-foreground">Protocol</p>
                <p className="font-bold">{scan.ssl_results.protocol || 'TLS 1.2/1.3'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Host</p>
                <p className="font-bold">{scan.ssl_results.host || scan.domain}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <p className="font-bold text-primary">{scan.ssl_results.status || 'Ready'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Endpoints</p>
                <p className="font-bold">{scan.ssl_results.endpoints?.length || 1}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Observatory Details */}
      {scan.observatory_results && !scan.observatory_results.error && scan.observatory_results.tests_passed !== undefined && (
        <div className="space-y-3">
          <h4 className="text-md font-mono font-bold flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Security Headers Analysis
          </h4>
          <div className="bg-background border border-border rounded-md p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-mono">
              <div>
                <p className="text-muted-foreground">Tests Passed</p>
                <p className="font-bold text-primary">{scan.observatory_results.tests_passed || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Tests Failed</p>
                <p className="font-bold text-destructive">{scan.observatory_results.tests_failed || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Score</p>
                <p className="font-bold">{scan.observatory_results.score || 0}/100</p>
              </div>
              <div>
                <p className="text-muted-foreground">Grade</p>
                <p className={`font-bold ${getGradeColor(scan.observatory_results.grade)}`}>
                  {scan.observatory_results.grade || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message if good scores */}
      {scan.overall_score >= 80 && (
        <div className="bg-primary/5 border border-primary/20 rounded-md p-6 text-center">
          <CheckCircle className="h-12 w-12 text-primary mx-auto mb-3" />
          <h4 className="font-mono font-bold text-lg mb-2">Good Security Posture</h4>
          <p className="text-sm text-muted-foreground">
            Your website has a strong security configuration. Keep monitoring for any changes.
          </p>
        </div>
      )}
    </div>
  );
}

export default VulnerabilityScanning;
